import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { catchError, map, merge, share, shareReplay, tap } from 'rxjs/operators';
import { AuthenticationService } from '../../core/services/authentication.service';
import { ComponentMeta } from '../../shared/component/component-meta';
import { Page } from '../../shared/page/page';
import { WidgetMeta } from '../../shared/widget/widget';
import { environment } from './../../../environments/environment';
import { LogCategory, LogCoreService, LoggerService, LogType } from './logger.service';
import { UtilityService } from './utility.service';

@Injectable({
    providedIn: 'root'
})
export class ProfileService {

    private _photoCacheBreaker: number;
   // private _reload$ = new Subject<void>();    

    // All the widgets available to the current user as define by his profile 
    public widgetStore: any;

    // All the widgets placements set by the user as found in their profile 
    public widgetPlacements: any;

    // All the beta widgets 
    public widgetBetas: any;

    // Lookup of all the widgets with the name as key and with label as value that area available to the current user as define by his profile 
    public labelLookup: any;
    public widgetLabelLookup: any;

    // All the pages available to the current user as defined by her profile
    public pageStore: any;

    //V2 Components - available widgets and pages for v2 ui
    public componentStore: any;   

    //
    public editStatus: boolean = false;

    public settings: any;

    public get profile() {
        return this._profileSubject.value ? this._profileSubject.value : null;
    }
    // Returns the Program and Program group of the current profile, or and empty string if there is no loaded profile 
    public get program(): string {
        return this._profileSubject.value ? this._profileSubject.value.academic.program : '';
    }
    public get programgroup(): string {
        return this._profileSubject.value ? this._profileSubject.value.academic.programgroup : '';
    }
    public get programFamilyFriendly(): string {
        return this._profileSubject.value ? this._profileSubject.value.academic.programFamilyFriendly : '';
    }
    public get programFriendly(): string {
        return this._profileSubject.value ? this._profileSubject.value.academic.programFriendly : '';
    }
    public get hasStops(): boolean {
        return this._profileSubject.value ? this._profileSubject.value.biodata.hasStops : false;
    }
    //public get theme(): boolean {
    //    return this._profileSubject.value ? this._profileSubject.value.theme : 'theme-default';
    //}
    //public get availablethemes(): boolean {
    //    return this._profileSubject.value ? this._profileSubject.value.availablethemes : 'default';
    //}

    public get searchFilter(): string {
        // searchFilter blank = not yet set
        // searchFilter 'n' = no filter items selected
        // searchFilter '[p][c][w]' = person, course, wordpress
        if (this._profileSubject.value) {
            if (this._profileSubject.value.searchFilter == '')
                this.setSearchFilter('p,c,w'); //set default to no selection

            return this._profileSubject.value.searchFilter;
        }
        else
            return 'p,c,w'; // default return if no profile subject
    }
    public setSearchFilter(value: string) {
        this._profileSubject.value.searchFilter = value;
    }

    public isUpdating: string[] = [];

    // Concentration data suitable for viewing only, as fetched by either the profile or the concentration editing screen 
    private _viewConcentrations$: Observable<any>; 
    public get viewConcentrations$(): Observable<any> {
        if (this._viewConcentrations$ == null) {
            this._viewConcentrations$ = this.profile$
                .pipe(
                map((profileData) => { return {
                        concentrations: profileData.academic.concentrations,
                        userMayEditConcentration: profileData.academic.userMayEditConcentration };
                    }),
                    merge(this._editConcentrationSubject),
                    tap((data) => {
                        this.log("viewConcentrations$", "new viewable concentration data", LogCategory.SubscriptionData);
                    }),
                    shareReplay()
                );
        }
        return this._viewConcentrations$;
    }

    // Concentration data suitable for updating, including things like valid options. This is kept up to date by actions
    // related to refreshing or updating concentrations. This returns a CLONE of concentration data in the subject so
    // the values can be bound without affecting the viewable version of concentrations. 
    private _editConcentrationSubject: Subject<any> = new Subject<any>();
    private _editConcentration$: Observable<any>;
    public get editConcentrations$(): Observable<any> {
        if (this._editConcentration$ == null) {
            this._editConcentration$ = this._editConcentrationSubject
                .pipe(
                    map((data) => JSON.parse( JSON.stringify(data))),
                    tap((data) => {
                        this.log("editConcentrations$", "new clone of editable concentration data", LogCategory.SubscriptionData);
                    }),
                );
        }

        return this._editConcentration$;
    }

    // Returns the observable of the profile subject, the currently loaded user profile
    private _profileSubject: BehaviorSubject<any> = new BehaviorSubject(null);
    private _profile$: Observable<any>; 
    public get profile$(): Observable<any> {
        if (this._profile$ == null) {
            this._profile$ = this._profileSubject.pipe(
                tap((res) => {
                    if (res?.biodata) {
                        res.biodata.photoUrl = this.getPhotoUrl(res.biodata.id);
                        res.biodata.failedPhotoUrl = 'assets/images/profile_well.png';
                    }
                    this.log("profile$", "profile$ reference created", LogCategory.SubscriptionData)
                })
            ) as Observable<any>;
        }
        return this._profile$;
    }

    private _contactInfo$: Observable<any> = null;
    public get contactInfo$(): Observable<any> {
        if (!this._contactInfo$) {
            this._contactInfo$ = this.http.get<any>(environment.apiUrl + '/profile/contactInfo')
                .pipe(
                    tap((data) => this.log('contactInfo$', 'Fetched address data', LogCategory.SubscriptionData)),                   
                    shareReplay(1)
                );
        }
        return this._contactInfo$;
    }
    
    constructor(
        private http: HttpClient,
        private auth: AuthenticationService,
        private logger: LoggerService,
        private utility: UtilityService
    ) {
        
        this.log("constructor", null, LogCategory.Constructor);
        this._photoCacheBreaker = new Date().getTime();           
        // Automatically refetch the whole profile whenever the user changes -- we're storing widget-related
        // data here, so we can't rely on subscriptions for this. We don't need to unsubscribe because services
        // don't get destroyed.  
        auth.loggedInUser$.subscribe((data) => {
            this.log("constructor", `Loggedin user changed to ${data}`, LogCategory.SubscriptionData);
            if (data > 0) {
                if (auth.isSpoofingAudience)
                    this.updateSpoofingAudience(auth.audience);
                else
                    this.updateSpoofingAudience('');
            }
                     
            // no need to fetch the profile if the logged in user changed to zero, but we should clear it.
            // This prevents repeated profile fetches (which could trigger things like repeated refreshes
            // or repeated initializations [with multiple sets of the same widgets] in the case of the
            // widgets host directive).           
            if (data === 0)
                this._profileSubject.next(null);
            else 
                this.refetchProfile();
        }); 
    }
    private log(method: string = null, message = null, category: LogCategory = LogCategory.Default): void {
        method = (method) ? '.' + method + '()' : '';
        message = (message) ? message.toString() : '';
        this.logger.try(this.constructor.name + method + ': ' + message, LogType.CoreService, LogCoreService.Profile,  category);
    }

    // Refetch the profile, recreate the widget store, and update the profile subject to alert any observers
    private refetchProfile() {
        this.log('refetchProfile');        
        // Get the profile. We don't need to unsubscribe to a finite observable like http.get() returns
        const url = `${environment.apiUrl}/profile`;
        this.http.get<any>(url).subscribe((data) => {
            this.log('refetchProfile', 'Profile received', LogCategory.SubscriptionData);
            //we need these loaded regardless of version so we can switch between v1 and v2 dashboard
            this.widgetStore = this.createStore('widget', data.widgets); 
            this.pageStore = this.createStore('page', data.pages);
            this.widgetLabelLookup = data.widgets.labelLookup; //this is needed for the profile page to load properly, can be removed when we switch to v2
            this.componentStore = this.createStore('component', data.components);
            this.settings = data.settings;
            if (data.version < 2) {                
                this.widgetPlacements = data.widgets.placement;
                this.widgetBetas = data.widgets.betas;                
                this.labelLookup = data.widgets.labelLookup;                
            } else {                        
                this.labelLookup = data.components.labelLookup;
                this.widgetBetas = data.components.betas;
                this.widgetPlacements = {};
            }          
           
            this._profileSubject.next(data);
        });
        
       // this._reload$.next();
    }

    // Updates widget-related preference data for a specific widget
    public updateWidgetPreference(data: WidgetMeta) {
        const url = `${environment.apiUrl}/profile/widgets/${data.Name}`;
        this.log("updateWidgetPreference", url);

        // No need to unsubscribe from finite observables like http 
        return this.http
            .put<any>(url, data)
            .subscribe(
                () => this.log("updateWidgetPreference", "updated widget", LogCategory.SubscriptionData),
                error => this.log("updateWidgetPreference", error, LogCategory.SubscriptionData)
            );
    }

    // Updates the widget placements for the given page
    public updateWidgetPlacement(page: string, widgetNames : string[] ) {
        const url = `${environment.apiUrl}/profile/widgets/page/${page}`;
        this.log("updateWidgetPlacement", `${url}; Page: ${page}; Value: ${widgetNames}`);

        // No need to unsubscribe from finite observables like http 
        this.http
            .put<any>(url, widgetNames)
            .subscribe(() => {
                this.log("updateWidgetPlacement", `Updated widget placement for page ${page} with ${widgetNames}`, LogCategory.SubscriptionData);
                //Set widge placements to new order set by the user
                this.widgetPlacements[page] = widgetNames;
            });
    }

    // Updates spoofing audience in profile
    public updateSpoofingAudience(audience: string) {
       const url = `${environment.apiUrl}/profile/audience`;
       this.log("updateWidgetPreference", url);
       const httpOptions = {
        headers: new HttpHeaders({
            'Content-Type': 'application/json',
            })
        };
     
        // No need to unsubscribe from finite observables like http 
        return this.http
            .post<string>(url, JSON.stringify( audience ) , httpOptions)
            .subscribe(
                () => this.log("updateSpoofingAudience", "updated spoofing audience setting", LogCategory.SubscriptionData),
                error => this.log("updateSpoofingAudience", error, LogCategory.SubscriptionData)
            );
    }

    // Updates all the profile settings with the names/values given in data 
    public updateSettings(data, refetch: boolean = true, redirect: boolean = false) {
        const url = `${environment.apiUrl}/profile/settings`;
        this.log("updateSettings", url);      
        // No need to unsubscribe from finite observables like http 
        this.http
            .post<any>(url, data)
            .subscribe(
                () => this.log("updateSettings", "updated profile settings", LogCategory.SubscriptionData),
                error => this.log("updateSettings", error, LogCategory.SubscriptionData),
                // This is here to refresh to profile widget when the user clicks save on the profile.  We shouldn't
                // perform a refetch when it involves widgets as it will cause duplication.
                () => {
                    if (refetch)
                        this.refetchProfile();
                    if (redirect)
                        window.location.href = '../../';
                }
        );
        
    }
   
    public updatePagePreferences(pageName: string, data: Page) {
        const url = `${environment.apiUrl}/profile/pages/${pageName}`;       
        this.log("updatePagePreference", url);
       
        // No need to unsubscribe from finite observables like http 
        return this.http.put<any>(url, data)
            .subscribe(
                () => this.log("updatePagePreferences", "updated profile settings for page", LogCategory.SubscriptionData),
                error => this.log("updatePagePreferences", error, LogCategory.SubscriptionData)
            );
    }

    // Updates component-related preference data
    public updateComponentPreference(name: string, data: any) {
        const url = `${environment.apiUrl}/profile/components/${name}`;
        this.log("updateComponentPreference", url);

        // No need to unsubscribe from finite observables like http 
        return this.http
            .put<any>(url, data)
            .subscribe(
                () => this.log("updateComponentPreference", "updated component", LogCategory.SubscriptionData),
                error => this.log("updateComponentPreference", error, LogCategory.SubscriptionData)
            );
    }

    // Update profile photo  
    public updatePhoto(data) {
        const url = `${environment.apiUrl}/profile/photo`;
        this.log("updatePhoto", url);

        // No need to save subscriptions for finite observable 
        return this.http
            .put<any>(url, { image: data })
            .pipe(
                tap(a => {
                    // Update the photo cache breaker to cause the photo to be reloaded 
                    this._photoCacheBreaker = new Date().getTime();
                    this.log("updatePhoto", "Profile photo updated.", LogCategory.SubscriptionData);
                    this._profileSubject.value.biodata.photoUrl = this.getPhotoUrl();
                }))
            .subscribe(); 
    }

    // Update profile photo  
    public deletePhoto() {
        const url = `${environment.apiUrl}/profile/photo`;
        this.log("deletePhoto", url);

        // No need to save subscriptions for finite observable 
        return this.http
            .delete<any>(url)
            .pipe(
                tap(a => {
                    // Update the photo cache breaker to cause the photo to be reloaded 
                    this._photoCacheBreaker = new Date().getTime();
                    this.log("deletePhoto", "Profile photo deleted.", LogCategory.SubscriptionData);
                    this._profileSubject.value.biodata.photoUrl = this.getPhotoUrl();
                }))
            .subscribe();
    }

    public get photoUrl() {
        return this.getPhotoUrl(); 
    }
    public getPhotoUrl(id: number = null) {
        // If we're getting an ID, then return the URL for that ID
        if ( id )
            return `${environment.apiUrl}/profile/${id}/photo`;
        // If we don't have any profile data yet, use the unknown image URL.
        else if (this._profileSubject.value && !!this._profileSubject.value.biodata.id) {
            return `${environment.apiUrl}/profile/${this._profileSubject.value.biodata.id}/photo?ts=${this._photoCacheBreaker}`
        }
        else {
            return `${environment.apiUrl}/profile/0/photo`;
        }
    }

    // Searches profile entries of the given personIds for the given search phrase in the public profile keys
    public searchProfiles$(search: string, category: string, personIds : number[], isSearchRefined: boolean = false) {
        const url = `${environment.apiUrl}/profile/search`;

        // No need to unsubscribe from finite observables like http 
        return this.http
            .post<any>(url, { search, category, personIds, isSearchRefined });
    }


    // Returns BioBooks    
    public bioBookDownloading: boolean[] = [false];
    public getBiobook( sectionId: number ) {       
        this.bioBookDownloading[sectionId] = true;
        const url = `${environment.apiUrl}/profile/biobook?sectionId=${sectionId}`;
        return this.http
            .get(url, { responseType: 'blob', observe: 'response' })
            .pipe(
                tap((res: HttpResponse<Blob>) => {                        
                    var file = {
                        filename: res.headers.get('Content-Disposition'),
                        body: new Blob([res.body], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' })
                    };
                    let matches = /filename="(.*?)"/g.exec(file.filename);                        
                    var filename = matches && matches.length > 1 ? matches[1] : "biobook";
                    var url = window.URL.createObjectURL(file.body);
                    this.bioBookDownloading[sectionId] = false;
                    //  Blobs do not have file names.  This allows for an automatic download of the file while
                    //  retaining the original file name.
                    this.utility.downloadFile(`${filename}`, url);
                    return res;
                        
                    }),
                catchError((err) => {
                    this.bioBookDownloading[sectionId] = false;                   
                    return of(err);
                })
            ).subscribe();
    }
   
    public rosterDownloading: boolean[] = [false];
    public downloadRoster$(sectionId) {
        this.rosterDownloading[sectionId] = true;  
        const url = `${environment.apiUrl}/profile/roster/${sectionId}`;
        return this.http.get(url, { responseType: 'blob', observe: 'response' }).pipe(
            share(),
            tap((res: HttpResponse<Blob>) => {
                var file = {
                    filename: res.headers.get('Content-Disposition'),
                    body: new Blob([res.body], { type: 'text/csv' })
                };

                let matches = /filename="(.*?)"/g.exec(file.filename);
                var filename = matches && matches.length > 1 ? matches[1] : "roster";
                var url = window.URL.createObjectURL(file.body);
                this.rosterDownloading[sectionId] = false;
                //  Blobs do not have file names.  This allows for an automatic download of the file while
                //  retaining the original file name.
                this.utility.downloadFile(`${filename}.csv`, url);

                return res;
            }),
            catchError((err) => {
                return of(err);
            })
        );
    }


   
    //Fetches a particular profile based on the personId
    public fetchProfile$(personId) {
        return this.http.get<any>(environment.apiUrl + '/profile/' + personId);
    }

    // Fetches concentration data for the current user 
    public saveConcentration(data) {

        const primary = (data.concentrations[0]) ? data.concentrations[0].Value : ''; 
        const secondary = (data.concentrations[1]) ? data.concentrations[1].Value : '';

        this.isUpdating.push('conc');

        // No need to save subscriptions for finite observable 
        return this.http
            .put<any>(`${environment.apiUrl}/profile/concentrations`, { primary, secondary })
            .subscribe((value) => {
                this._profileSubject.value.academic.concentrations = data.concentrations;
                this.refetchConcentrationData();
                // Update the concentration data subject if successful 
                this._editConcentrationSubject.next(data); 
                this.log("saveConcentration", "Concentrations updated.", LogCategory.SubscriptionData);
                this.isUpdating = this.isUpdating.filter(i => i != 'conc');
            } )
    }

    // Refetches the concentration data and re-emits it
    public refetchConcentrationData() {
        this.http.get<any>(`${environment.apiUrl}/profile/concentrations`)
            .subscribe((data) => {
                // Update the concentration data subject if successful 
                this._editConcentrationSubject.next(data);
                this.log("refetchConcentrationData", "Concentration data refetched.", LogCategory.SubscriptionData);
            })
    }

    private createStore(storeType: string, data: any) {
        let availableItems = data.available.split(",");
        let store = {};
        let meta;
        for (let i = 0; i < availableItems.length; i++) {
            if (!store[availableItems[i]]) {
                switch (storeType) {
                    case "widget":
                        meta = new WidgetMeta(availableItems[i]);
                        meta.copyFrom(data.prefs[availableItems[i]]);
                        break;
                    case "page":
                        meta = new Page(availableItems[i]);
                        meta.copyFrom(data.prefs[availableItems[i]]);
                        break;
                    case "component":
                        meta = new ComponentMeta(availableItems[i]);
                        var metaData = {
                            IsBeta: data.betas.includes(availableItems[i]),
                            IsModal: data.modals.includes(availableItems[i]),
                            Data: data.data[availableItems[i]]
                        }
                        meta.copyFrom(metaData);
                }                
                store[availableItems[i]] = meta;
            }
        }
        return store;       
    }
        
}
